import {
    Component,
    OnInit,
    AfterViewInit,
    AfterViewChecked,
    OnDestroy,
    ViewChild,
    ElementRef,
    Inject,
    LOCALE_ID,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Title, Meta, SafeHtml, DomSanitizer } from '@angular/platform-browser';
import * as L from 'leaflet';
import { MainConfig } from '../../conf/main.config';
import { ProgramsResolve } from '../programs/programs-resolve.service';
import { Program } from '../programs/programs.models';
import { ObservationsService } from '../programs/observations/observations.service';
import { AuthService } from '../auth/auth.service';
import { LoginComponent } from '../auth/login/login.component';
import { RegisterComponent } from '../auth/register/register.component';
import { ModalsTopbarService } from '../core/topbar/modalTopbar.service';
import {
    Stats,
    TaxonomyListItem,
    ObservationFeature,
} from '../programs/observations/observation.model';

interface RegionState {
    code: string;
    name: string;
    bbox: [number, number, number, number];
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    providers: [ProgramsResolve],
})
export class HomeComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    programs: Program[] = [];
    fragment: string;
    platform_teaser: SafeHtml;
    platform_intro: SafeHtml;
    MainConfig = MainConfig;
    stats: Stats;

    catalogTaxa: TaxonomyListItem[] = [];
    featuredTaxa: TaxonomyListItem[] = [];
    allObservations: ObservationFeature[] = [];
    recentObservations: ObservationFeature[] = [];

    speciesTab: 'featured' | 'most' = 'featured';
    catalogLoading = true;
    observationsLoading = true;
    catalogError = false;
    observationsError = false;

    searchQuery = '';
    searchTouched = false;

    regionState = '';
    regionSpeciesQuery = '';
    regionSelectedTaxon: TaxonomyListItem = null;
    regionView: 'map' | 'list' = 'map';

    currentLanguage = 'pt-BR';

    @ViewChild('brazilMap', { static: false }) brazilMapRef: ElementRef;
    private brazilMap: L.Map;
    private brazilObservationLayer: L.LayerGroup;

    readonly states: RegionState[] = [
        { code: 'AC', name: 'Acre', bbox: [-11.15, -73.99, -7.10, -66.62] },
        { code: 'AL', name: 'Alagoas', bbox: [-10.50, -36.70, -8.80, -35.10] },
        { code: 'AP', name: 'Amapá', bbox: [0.80, -54.90, 4.50, -49.90] },
        { code: 'AM', name: 'Amazonas', bbox: [-9.84, -73.80, 2.25, -56.10] },
        { code: 'BA', name: 'Bahia', bbox: [-18.35, -46.70, -8.53, -37.30] },
        { code: 'CE', name: 'Ceará', bbox: [-7.86, -41.40, -2.78, -37.20] },
        { code: 'DF', name: 'Distrito Federal', bbox: [-16.10, -48.30, -15.50, -47.30] },
        { code: 'ES', name: 'Espírito Santo', bbox: [-21.30, -41.90, -17.90, -39.60] },
        { code: 'GO', name: 'Goiás', bbox: [-19.50, -53.30, -12.40, -45.90] },
        { code: 'MA', name: 'Maranhão', bbox: [-10.30, -48.80, -1.00, -41.80] },
        { code: 'MT', name: 'Mato Grosso', bbox: [-18.00, -61.60, -7.35, -50.20] },
        { code: 'MS', name: 'Mato Grosso do Sul', bbox: [-24.10, -58.20, -17.10, -50.90] },
        { code: 'MG', name: 'Minas Gerais', bbox: [-22.90, -51.00, -14.20, -39.80] },
        { code: 'PA', name: 'Pará', bbox: [-9.90, -58.90, 2.60, -46.00] },
        { code: 'PB', name: 'Paraíba', bbox: [-8.30, -38.80, -6.00, -34.80] },
        { code: 'PR', name: 'Paraná', bbox: [-26.70, -54.70, -22.50, -48.00] },
        { code: 'PE', name: 'Pernambuco', bbox: [-9.50, -41.40, -7.10, -34.80] },
        { code: 'PI', name: 'Piauí', bbox: [-10.90, -45.90, -2.70, -40.40] },
        { code: 'RJ', name: 'Rio de Janeiro', bbox: [-23.40, -44.90, -20.70, -40.90] },
        { code: 'RN', name: 'Rio Grande do Norte', bbox: [-6.98, -38.60, -4.80, -34.96] },
        { code: 'RS', name: 'Rio Grande do Sul', bbox: [-33.75, -57.70, -27.10, -49.70] },
        { code: 'RO', name: 'Rondônia', bbox: [-13.70, -66.80, -7.97, -59.80] },
        { code: 'RR', name: 'Roraima', bbox: [0.90, -64.80, 5.30, -58.90] },
        { code: 'SC', name: 'Santa Catarina', bbox: [-29.40, -53.80, -25.95, -48.30] },
        { code: 'SP', name: 'São Paulo', bbox: [-25.30, -53.10, -19.80, -44.10] },
        { code: 'SE', name: 'Sergipe', bbox: [-11.60, -38.30, -9.50, -36.40] },
        { code: 'TO', name: 'Tocantins', bbox: [-13.50, -50.80, -5.20, -45.70] },
    ];

    constructor(
        @Inject(LOCALE_ID) readonly localeId: string,
        private router: Router,
        private route: ActivatedRoute,
        private metaTagService: Meta,
        private titleService: Title,
        private observationsService: ObservationsService,
        private auth: AuthService,
        private modalService: ModalsTopbarService,
        protected domSanitizer: DomSanitizer,
        protected http: HttpClient
    ) {
        this.currentLanguage = localStorage.getItem('language') === 'fr' ? 'fr' : 'pt-BR';
    }

    isLoggedIn(): Observable<boolean> {
        return this.auth.isLoggedIn();
    }

    openLogin(): void {
        this.modalService.open(LoginComponent, {
            size: 'lg',
            centered: true,
            windowClass: 'wikiprimes-login-modal',
            backdropClass: 'wikiprimes-login-backdrop',
        });
        if (this.modalService.modalRef) {
            this.modalService.modalRef.result.catch((reason) => {
                if (reason === 'switch-register') {
                    this.openRegister();
                }
            });
        }
    }

    openRegister(): void {
        this.modalService.open(RegisterComponent, {
            size: 'lg',
            centered: true,
            windowClass: 'wikiprimes-register-modal',
            backdropClass: 'wikiprimes-register-backdrop',
        });
        if (this.modalService.modalRef) {
            this.modalService.modalRef.result.catch((reason) => {
                if (reason === 'switch-login') {
                    this.openLogin();
                }
            });
        }
    }

    ngOnInit(): void {
        this.route.data.subscribe((data: { programs: Program[] }) => {
            this.programs = data.programs || [];
            this.observationsService.getStat().subscribe(
                (stats) => (this.stats = stats),
                () => (this.stats = null)
            );
        });

        this.route.fragment.subscribe((fragment) => (this.fragment = fragment));

        this.loadRecentObservations();

        this.http
            .get<TaxonomyListItem[]>(
                MainConfig.API_ENDPOINT + '/taxonomy/lists/101/species',
                { params: { limit: '300' } }
            )
            .subscribe(
                (taxa) => {
                    this.catalogTaxa = taxa || [];
                    this.catalogLoading = false;
                    this.catalogError = false;
                    this.updateFeaturedTaxa();
                },
                () => {
                    this.catalogTaxa = [];
                    this.catalogLoading = false;
                    this.catalogError = true;
                }
            );

        this.metaTagService.updateTag({
            name: 'description',
            content: 'WikiPrimes — comunidade de observação, ciência e registros de primatas.',
        });
        this.metaTagService.updateTag({
            property: 'og:title',
            content: 'WikiPrimes',
        });
        this.metaTagService.updateTag({
            property: 'og:description',
            content: 'Explore espécies, compartilhe fotos e contribua para conhecer os primatas do Brasil.',
        });
        this.titleService.setTitle('WikiPrimes');

        this.platform_intro = this.domSanitizer.bypassSecurityTrustHtml('WikiPrimes');
        this.platform_teaser = this.domSanitizer.bypassSecurityTrustHtml(
            this.t('Observação, ciência e comunidade.', 'Observation, science et communauté.')
        );
    }

    /**
     * Recarrega as observações da galeria sem confundir uma lista vazia com falha de API.
     * Oferece recuperação manual após falhas transitórias de rede.
     */
    loadRecentObservations(): void {
        this.observationsLoading = true;
        this.observationsError = false;
        this.observationsService.getRecentObservations(500).subscribe(
            (observations) => {
                this.allObservations = observations && observations.features || [];
                this.recentObservations = this.allObservations.slice(0, 30);
                this.observationsLoading = false;
                this.observationsError = false;
                this.updateFeaturedTaxa();
                this.renderObservationMarkers();
            },
            (error) => {
                console.error('[WikiPRIMATES] Falha ao consultar observações recentes:', error);
                this.allObservations = [];
                this.recentObservations = [];
                this.observationsLoading = false;
                this.observationsError = true;
                this.renderObservationMarkers();
            }
        );
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.initBrazilMap(), 0);
    }

    t(portuguese: string, french: string): string {
        return this.currentLanguage === 'fr' ? french : portuguese;
    }

    setSpeciesTab(tab: 'featured' | 'most'): void {
        this.speciesTab = tab;
        this.updateFeaturedTaxa();
    }

    private updateFeaturedTaxa(): void {
        if (!this.catalogTaxa.length) {
            this.featuredTaxa = [];
            return;
        }

        const copy = this.catalogTaxa.slice();
        if (this.speciesTab === 'most') {
            copy.sort((a, b) => this.getTaxonCount(b) - this.getTaxonCount(a));
        } else {
            copy.sort((a, b) => {
                const photoDifference =
                    Number(!!this.getTaxonPhoto(b)) - Number(!!this.getTaxonPhoto(a));
                if (photoDifference !== 0) {
                    return photoDifference;
                }
                const aName = (a.taxref && (a.taxref.nom_vern || a.taxref.lb_nom)) || '';
                const bName = (b.taxref && (b.taxref.nom_vern || b.taxref.lb_nom)) || '';
                return aName.localeCompare(bName);
            });
        }
        this.featuredTaxa = copy.slice(0, 4);
    }

    getTaxonCount(item: TaxonomyListItem): number {
        if (!item || !item.taxref) {
            return 0;
        }
        return this.allObservations.filter(
            (observation) => observation.properties.cd_nom === item.taxref.cd_nom
        ).length;
    }

    getTaxonPhoto(item: TaxonomyListItem): string {
        if (!item || !item.taxref) {
            return null;
        }
        if (item.medias && item.medias.length) {
            return (
                MainConfig.API_ENDPOINT +
                '/tmedias/thumbnail/' +
                item.medias[0].id_media +
                '?h=500'
            );
        }
        return null;
    }

    openTaxon(item: TaxonomyListItem): void {
        if (item && item.taxref) {
            this.router.navigate(['/synthesis/species', item.taxref.cd_nom]);
        }
    }

    onSearchInput(value: string): void {
        this.searchQuery = value || '';
        this.searchTouched = false;
    }

    submitSearch(): void {
        this.searchTouched = true;
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.searchTouched = false;
    }

    get searchSpeciesResults(): TaxonomyListItem[] {
        const query = this.normalize(this.searchQuery);
        if (query.length < 2) {
            return [];
        }
        return this.catalogTaxa
            .filter((item) => {
                const common = this.normalize(item.taxref && item.taxref.nom_vern);
                const scientific = this.normalize(item.taxref && item.taxref.lb_nom);
                return common.indexOf(query) >= 0 || scientific.indexOf(query) >= 0;
            })
            .slice(0, 5);
    }

    get searchObservationResults(): ObservationFeature[] {
        const query = this.normalize(this.searchQuery);
        if (query.length < 2) {
            return [];
        }
        return this.allObservations
            .filter((observation) => {
                const p = observation.properties;
                const taxref: any = p.taxref || {};
                return [
                    p.name,
                    taxref.nom_vern,
                    taxref.lb_nom,
                    this.getLocationLabel(observation),
                    p.observer && p.observer.username,
                ].some((value) => this.normalize(value).indexOf(query) >= 0);
            })
            .slice(0, 5);
    }

    get hasSearchResults(): boolean {
        return this.searchSpeciesResults.length > 0 || this.searchObservationResults.length > 0;
    }

    openObservation(observation: ObservationFeature): void {
        if (!observation || !observation.properties) {
            return;
        }
        this.router.navigate([
            '/programs',
            observation.properties.id_program || 1,
            'observations',
            observation.properties.id_observation,
        ]);
    }

    onRegionSpeciesInput(value: string): void {
        this.regionSpeciesQuery = value || '';
        if (
            this.regionSelectedTaxon &&
            this.regionSpeciesQuery !== this.getTaxonDisplayName(this.regionSelectedTaxon)
        ) {
            this.regionSelectedTaxon = null;
            this.applyRegionFilters(false);
        }
    }

    get regionSpeciesSuggestions(): TaxonomyListItem[] {
        const query = this.normalize(this.regionSpeciesQuery);
        if (query.length < 1 || this.regionSelectedTaxon) {
            return [];
        }
        return this.catalogTaxa
            .filter((item) => {
                const common = this.normalize(item.taxref && item.taxref.nom_vern);
                const scientific = this.normalize(item.taxref && item.taxref.lb_nom);
                return common.indexOf(query) >= 0 || scientific.indexOf(query) >= 0;
            })
            .slice(0, 7);
    }

    selectRegionTaxon(item: TaxonomyListItem): void {
        this.regionSelectedTaxon = item;
        this.regionSpeciesQuery = this.getTaxonDisplayName(item);
        this.applyRegionFilters(false);
    }

    clearRegionTaxon(): void {
        this.regionSelectedTaxon = null;
        this.regionSpeciesQuery = '';
        this.applyRegionFilters(false);
    }

    getTaxonDisplayName(item: TaxonomyListItem): string {
        if (!item || !item.taxref) {
            return '';
        }
        return item.taxref.nom_vern || item.taxref.lb_nom || '';
    }

    onRegionStateChange(value: string): void {
        this.regionState = value || '';
        this.applyRegionFilters(false);
    }

    applyRegionFilters(scroll: boolean = true): void {
        this.renderObservationMarkers();
        this.fitMapToRegion();
        if (scroll) {
            const region = document.querySelector('#regions');
            if (region) {
                region.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    setRegionView(view: 'map' | 'list'): void {
        this.regionView = view;
        if (view === 'map') {
            setTimeout(() => {
                if (this.brazilMap) {
                    this.brazilMap.invalidateSize();
                    this.fitMapToRegion();
                }
            }, 0);
        }
    }

    get filteredRegionObservations(): ObservationFeature[] {
        return this.allObservations.filter((observation) => {
            if (
                this.regionSelectedTaxon &&
                observation.properties.cd_nom !== this.regionSelectedTaxon.taxref.cd_nom
            ) {
                return false;
            }
            if (this.regionState && !this.observationInsideState(observation, this.regionState)) {
                return false;
            }
            return true;
        });
    }

    private observationInsideState(observation: ObservationFeature, code: string): boolean {
        const state = this.states.find((item) => item.code === code);
        const coordinates = this.getObservationCoordinates(observation);
        if (!state || !coordinates) {
            return false;
        }
        const lat = coordinates[0];
        const lng = coordinates[1];
        return (
            lat >= state.bbox[0] &&
            lat <= state.bbox[2] &&
            lng >= state.bbox[1] &&
            lng <= state.bbox[3]
        );
    }

    private initBrazilMap(): void {
        if (!this.brazilMapRef || this.brazilMap) {
            return;
        }

        this.brazilMap = L.map(this.brazilMapRef.nativeElement, {
            zoomControl: true,
            attributionControl: true,
            scrollWheelZoom: false,
            minZoom: 3,
            maxZoom: 18,
        });

        L.tileLayer('//{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(this.brazilMap);

        this.brazilObservationLayer = L.layerGroup().addTo(this.brazilMap);
        this.fitMapToRegion();
        this.renderObservationMarkers();

        setTimeout(() => {
            if (this.brazilMap) {
                this.brazilMap.invalidateSize();
            }
        }, 180);
    }

    private fitMapToRegion(): void {
        if (!this.brazilMap) {
            return;
        }
        if (this.regionState) {
            const state = this.states.find((item) => item.code === this.regionState);
            if (state) {
                this.brazilMap.fitBounds(
                    L.latLngBounds(
                        L.latLng(state.bbox[0], state.bbox[1]),
                        L.latLng(state.bbox[2], state.bbox[3])
                    ),
                    { padding: [14, 14] }
                );
                return;
            }
        }

        const filteredCoordinates = this.filteredRegionObservations
            .map((observation) => this.getObservationCoordinates(observation))
            .filter((coordinates) => !!coordinates);

        if (this.regionSelectedTaxon && filteredCoordinates.length) {
            this.brazilMap.fitBounds(
                L.latLngBounds(filteredCoordinates.map((coords) => L.latLng(coords[0], coords[1]))),
                { padding: [36, 36], maxZoom: 8 }
            );
            return;
        }

        this.brazilMap.setView(L.latLng(-14.2, -52.8), 4);
        this.brazilMap.setMaxBounds(
            L.latLngBounds(L.latLng(-42, -82), L.latLng(13, -26))
        );
    }

    private renderObservationMarkers(): void {
        if (!this.brazilMap || !this.brazilObservationLayer) {
            return;
        }

        this.brazilObservationLayer.clearLayers();

        this.filteredRegionObservations.forEach((observation) => {
            const coordinates = this.getObservationCoordinates(observation);
            if (!coordinates) {
                return;
            }

            const marker = L.circleMarker(coordinates, {
                radius: 7,
                color: '#ffffff',
                weight: 2,
                fillColor: '#193d30',
                fillOpacity: 0.95,
            });

            const p = observation.properties;
            const speciesName = this.escapeHtml(this.getObservationName(observation));
            const municipality = this.escapeHtml(this.getLocationLabel(observation));
            const detailUrl =
                '/programs/' +
                (p.id_program || 1) +
                '/observations/' +
                p.id_observation;

            marker.bindPopup(
                '<div class="wp-map-popup"><strong>' +
                    speciesName +
                    '</strong><span>' +
                    municipality +
                    '</span><a href="' +
                    detailUrl +
                    '">' +
                    this.t('Ver registro', "Voir l'observation") +
                    '</a></div>'
            );
            marker.addTo(this.brazilObservationLayer);
        });
    }

    private getObservationCoordinates(observation: ObservationFeature): [number, number] {
        const geometry: any = observation && observation.geometry;
        if (
            !geometry ||
            geometry.type !== 'Point' ||
            !geometry.coordinates ||
            geometry.coordinates.length < 2
        ) {
            return null;
        }
        const longitude = Number(geometry.coordinates[0]);
        const latitude = Number(geometry.coordinates[1]);
        if (!isFinite(latitude) || !isFinite(longitude)) {
            return null;
        }
        return [latitude, longitude];
    }

    get recentPhotoObservations(): ObservationFeature[] {
        return (this.recentObservations || [])
            .filter((observation) => !!this.getObservationPhoto(observation))
            .slice(0, 9);
    }

    get recentPortraitObservations(): ObservationFeature[] {
        return this.recentPhotoObservations.slice(0, 5);
    }

    get recentLandscapeObservations(): ObservationFeature[] {
        return this.recentPhotoObservations.slice(5, 9);
    }

    getObservationPhoto(observation: ObservationFeature): string {
        if (!observation || !observation.properties) {
            return null;
        }
        if (
            observation.properties.photos &&
            observation.properties.photos.length > 0
        ) {
            const photoUrl = observation.properties.photos[0].url;
            if (photoUrl && /^https?:\/\//i.test(photoUrl)) {
                return photoUrl;
            }
            return photoUrl ? MainConfig.API_ENDPOINT + photoUrl : null;
        }
        if (
            observation.properties.images &&
            observation.properties.images.length > 0
        ) {
            return (
                MainConfig.API_ENDPOINT +
                '/media/' +
                observation.properties.images[0]
            );
        }
        if ((observation.properties as any).image) {
            const imageUrl = (observation.properties as any).image;
            if (/^https?:\/\//i.test(imageUrl)) {
                return imageUrl;
            }
            return MainConfig.API_ENDPOINT + '/media/' + imageUrl;
        }
        if (
            observation.properties.medias &&
            observation.properties.medias.length > 0
        ) {
            return (
                MainConfig.API_ENDPOINT +
                '/tmedias/thumbnail/' +
                observation.properties.medias[0].id_media +
                '?h=500'
            );
        }
        return null;
    }

    getObservationName(observation: ObservationFeature): string {
        const taxref: any = observation && observation.properties
            ? observation.properties.taxref
            : null;
        return (
            (observation && observation.properties && observation.properties.name) ||
            (taxref && taxref.nom_vern) ||
            (taxref && taxref.lb_nom) ||
            this.t('Primata', 'Primate')
        );
    }

    getScientificName(observation: ObservationFeature): string {
        const taxref: any = observation && observation.properties
            ? observation.properties.taxref
            : null;
        return (taxref && taxref.lb_nom) || '';
    }

    getLocationLabel(observation: ObservationFeature): string {
        const municipality =
            observation && observation.properties
                ? observation.properties.municipality
                : null;
        if (
            !municipality ||
            municipality === 'None (None)' ||
            municipality === 'None' ||
            municipality === 'null'
        ) {
            return this.t('Local não informado', 'Lieu non renseigné');
        }
        return municipality;
    }

    getObserverName(observation: ObservationFeature): string {
        const observer =
            observation && observation.properties
                ? observation.properties.observer
                : null;
        return observer && observer.username
            ? observer.username
            : this.t('Observador não informado', 'Observateur non renseigné');
    }

    private normalize(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    private escapeHtml(value: string): string {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    ngAfterViewChecked(): void {
        if (!this.fragment) {
            return;
        }
        try {
            const element = document.querySelector('#' + this.fragment);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                this.fragment = null;
            }
        } catch (_e) {}
    }

    ngOnDestroy(): void {
        if (this.brazilMap) {
            this.brazilMap.off();
            this.brazilMap.remove();
            this.brazilMap = null;
        }
    }
}
